<template>
  <div>
    <FencingBanner
      v-if="incentivesFenced"
      :headline="t('Reviews.product.view.media.banner.incentivesFencingHeadline')"
      :feature-list="fencedFeatureList"
      :cta-label="t('Reviews.product.view.media.banner.incentivesFencingCtaLabel')"
      :cta-link="t('Reviews.product.view.media.banner.incentivesFencingCtaLink')"
      :banner-image="incentivesFencingImageUrl"
      placement="banner"
      entity-type="tool"
      :entity-id="product.id"
    />
    <div class="mb-8 grid grid-cols-2 gap-6 desktop:grid-cols-5">
      <VuelidateField :validation="v$.title">
        <template #default="{ error, state }">
          <FieldWrapper
            for="campaign-title"
            class="desktop:col-span-2"
            :label="t('Reviews.product.campaign.form.campaignName.input.label')"
            :label-hint="t('Reviews.product.campaign.form.campaignName.input.labelHint')"
            :error="error"
            :hint="t('Reviews.product.campaign.form.campaignName.input.hint', { min: NAME_MIN_LENGTH, max: NAME_MAX_LENGTH })"
          >
            <template #default="{ id }">
              <InputField
                :id="id"
                v-model="v$.title.$model"
                size="l"
                type="text"
                :maxlength="NAME_MAX_LENGTH"
                :state="state"
                :placeholder="t('Reviews.product.campaign.form.campaignName.input.placeholder')"
              />
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
    </div>
    <div>
      <div class="mb-1 text-body-s-bold text-main">{{ t('Reviews.product.campaign.form.locale.label') }}</div>
      <div class="mb-8 grid grid-cols-2 gap-x-6 gap-y-2 desktop:grid-cols-5">
        <VuelidateField :validation="v$.locale">
          <template #default="{ state }">
            <fieldset id="campaign-locale" class="flex gap-x-3 desktop:col-span-2">
              <RadioCard v-model="v$.locale.$model" :value="IncentivizationCampaignPersonalizationLocaleEnum.De" :state="state">
                {{ t('Reviews.product.campaign.form.locale.input.de') }}
              </RadioCard>
              <RadioCard v-model="v$.locale.$model" :value="IncentivizationCampaignPersonalizationLocaleEnum.En" :state="state">
                {{ t('Reviews.product.campaign.form.locale.input.en') }}
              </RadioCard>
            </fieldset>
            <div class="row-start-2 txt-label-m text-subtlest desktop:col-span-2">
              {{ t('Reviews.product.campaign.form.locale.hint') }}
            </div>
          </template>
        </VuelidateField>
      </div>
    </div>
    <div class="grid grid-cols-2 gap-6 desktop:grid-cols-5" :class="budgetEndDate ? 'mb-1' : 'mb-8'">
      <div class="flex gap-x-3 desktop:col-span-2">
        <VuelidateField :validation="v$.validFrom">
          <template #default="{ error, state }">
            <FieldWrapper for="campaign-valid-from" :label="t('Reviews.product.campaign.form.validFrom.input.label')" :error="error">
              <template #default="{ id }">
                <DateField :id="id" v-model="v$.validFrom.$model" size="l" type="date" :state="state" :min="today" />
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>
        <VuelidateField :validation="v$.validTo">
          <template #default="{ error, state }">
            <FieldWrapper for="campaign-valid-to" :label="t('Reviews.product.campaign.form.validTo.input.label')" :error="error" optional>
              <template #default="{ id }">
                <DateField
                  :id="id"
                  v-model="v$.validTo.$model"
                  size="l"
                  type="date"
                  :state="state"
                  :min="formData.validFrom || today"
                  :max="maxValidToDate"
                />
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>
      </div>
    </div>
    <div
      v-if="budgetEndDate"
      class="mb-10 w-1/2 pr-3 txt-label-m text-subtlest desktop:w-2/5"
      v-html="t('Reviews.product.campaign.form.validTo.hint', { date: budgetEndDateLong })"
    />
    <FeatureFencingWrapper
      :fenced="incentivesFenced"
      :label="t('Reviews.product.campaign.form.incentivesFencingLabel')"
      :help-tooltip="t('Reviews.product.campaign.form.incentivesFencingTooltip')"
      :upgrade-cta="t('Reviews.product.campaign.form.incentivesFencingUpgradeCta')"
      :upgrade-cta-link="t('Reviews.product.campaign.form.incentivesFencingUpgradeCtaLink')"
      placement="incentives"
      entity-type="tool"
      :entity-id="product.id"
    >
      <div class="mb-3 flex items-center gap-2 txt-headline-xl text-main" :class="{ hidden: incentivesFenced }">
        {{ t('Reviews.product.campaign.form.incentive.label') }}
      </div>
      <div class="mb-8 grid grid-cols-2 gap-6 txt-body-m desktop:grid-cols-5">
        <MarkdownContent
          class="flex flex-col gap-3"
          :class="incentivesFenced ? 'col-span-2 desktop:col-span-5' : 'desktop:col-span-2'"
          :unstyled="true"
          :md="incentiveDescription"
        />
      </div>
      <div class="grid grid-cols-2 gap-x-6 desktop:grid-cols-5" :class="{ 'mb-8': !incentivesFenced }">
        <div class="desktop:col-span-2">
          <VuelidateField :validation="v$.incentive">
            <template #default="{ error, state }">
              <FieldWrapper
                for="campaign-incentive-selection"
                :label="t('Reviews.product.campaign.form.incentive.input.label')"
                :error="error"
              >
                <template #default="{ id }">
                  <fieldset :id="id" class="flex flex-col gap-x-6 gap-y-2 desktop:col-span-2">
                    <RadioCard v-if="!newTypesAvailable" v-model="incentiveSelection" :value="null" :state="state">
                      {{ t('Reviews.product.campaign.form.incentiveSelection.input.noIncentive') }}
                    </RadioCard>
                    <TooltipWrapper v-if="!newTypesAvailable" class="w-full">
                      <template #trigger>
                        <RadioCard
                          v-model="incentiveSelection"
                          :value="IncentivizationCampaignIncentivizationTypeEnum.Wunsch"
                          :state="state"
                          :disabled="incentivesFenced"
                        >
                          {{ t('Reviews.product.campaign.form.incentiveSelection.input.wunsch', { value: wunschFormattedValue }) }}
                        </RadioCard>
                      </template>
                      <template v-if="incentivesFenced" #content>
                        <div class="max-w-xs">{{ t('Reviews.product.campaign.form.incentiveSelection.inputFenced.tooltip') }}</div>
                      </template>
                    </TooltipWrapper>
                    <TooltipWrapper v-if="!newTypesAvailable" class="w-full">
                      <template #trigger>
                        <VuelidateField :validation="v$.amount">
                          <template #default="{ error: inputError, state: inputState }">
                            <RadioCard
                              v-model="incentiveSelection"
                              :value="IncentivizationCampaignIncentivizationTypeEnum.Amazon"
                              :state="inputState"
                              :disabled="incentivesFenced"
                            >
                              <span :class="incentivesFenced ? 'text-disabled' : ''">
                                {{ t('Reviews.product.campaign.form.incentiveSelection.input.amazon', { value: amazonFormattedValue }) }}
                              </span>
                              <template #nested>
                                <FieldWrapper
                                  for="campaign-amazon-amount"
                                  :error="inputError"
                                  :class="{ 'text-disabled': incentivesFenced }"
                                >
                                  <template #default="{ id: inputId }">
                                    <SelectField
                                      :id="inputId"
                                      v-model.number="v$.amount.$model"
                                      :disabled="incentivesFenced"
                                      :state="inputState"
                                      :placeholder="t('Reviews.product.campaign.form.amount.input.placeholder')"
                                      @clear="v$.amount.$model = undefined"
                                    >
                                      <option v-for="option in amazonAmountOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                      </option>
                                    </SelectField>
                                  </template>
                                </FieldWrapper>
                              </template>
                            </RadioCard>
                          </template>
                        </VuelidateField>
                      </template>
                      <template v-if="incentivesFenced" #content>
                        <div class="max-w-xs">{{ t('Reviews.product.campaign.form.incentiveSelection.inputFenced.tooltip') }}</div>
                      </template>
                    </TooltipWrapper>
                    <VuelidateField v-for="card in newIncentiveTypeCards" :key="card.value" :validation="v$.amount">
                      <template #default="{ error: inputError, state: inputState }">
                        <RadioCard
                          v-model="incentiveSelection"
                          :value="card.value"
                          :state="card.hasAmount && incentiveSelection === card.value ? inputState : state"
                          :disabled="incentivesFenced"
                        >
                          <span :class="incentivesFenced ? 'text-disabled' : ''">
                            {{ t(card.labelKey, { value: amazonFormattedValue }) }}
                          </span>
                          <template v-if="card.hasAmount" #nested>
                            <FieldWrapper
                              :for="`campaign-${card.value}-amount`"
                              :error="inputError"
                              :class="{ 'text-disabled': incentivesFenced }"
                            >
                              <template #default="{ id: inputId }">
                                <SelectField
                                  :id="inputId"
                                  v-model.number="v$.amount.$model"
                                  :disabled="incentivesFenced"
                                  :state="inputState"
                                  :placeholder="t('Reviews.product.campaign.form.amount.input.placeholder')"
                                  @clear="v$.amount.$model = undefined"
                                >
                                  <option v-for="option in amazonAmountOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                  </option>
                                </SelectField>
                              </template>
                            </FieldWrapper>
                          </template>
                        </RadioCard>
                      </template>
                    </VuelidateField>
                  </fieldset>
                </template>
              </FieldWrapper>
            </template>
          </VuelidateField>
        </div>
        <div
          v-if="incentivesFenced"
          class="mt-5.5 ml-4 flex flex-col gap-2 rounded-sm border border-primary-subtle bg-primary-subtler p-4 desktop:col-span-3"
        >
          <div class="text-headline-xs text-main">{{ t('Reviews.product.campaign.incentivesVoucher.title') }}</div>
          <div class="text-main">{{ t('Reviews.product.campaign.incentivesVoucher.description') }}</div>
          <a :href="t('Reviews.product.campaign.incentivesVoucher.ctaLink')" target="_blank" class="btn-secondary-color-xs mt-auto w-fit">
            {{ t('Reviews.product.campaign.incentivesVoucher.ctaLabel') }}
            <GiftIcon />
          </a>
        </div>
      </div>

      <div v-if="formData.incentive && !isNoneSelected" class="mb-8 grid grid-cols-2 gap-6 desktop:grid-cols-5">
        <VuelidateField :validation="v$.limitType">
          <template #default="{ error, state }">
            <FieldWrapper
              for="campaign-limitType"
              class="desktop:col-span-2"
              :label="t('Reviews.product.campaign.form.limitType.input.label')"
              :error="error"
              :class="{ 'text-disabled': incentivesFenced }"
            >
              <template #default="{ id }">
                <fieldset :id="id" class="mt-2 flex w-full flex-col gap-y-3">
                  <RadioCard v-model="internalLimitSelection" value="useRemainingBudget" :state="state">
                    {{
                      t('Reviews.product.campaign.form.limitType.input.option.useRemainingBudget', {
                        amount: formatCurrency(remainingBudget),
                      })
                    }}
                  </RadioCard>
                  <VuelidateField :validation="v$.incentivizationsLimit">
                    <template #default="{ error: inputError, state: inputState }">
                      <RadioCard v-model="internalLimitSelection" value="setByReviews" :state="inputState">
                        {{ t('Reviews.product.campaign.form.limitType.input.option.setByReviews') }}
                        <template #nested>
                          <FieldWrapper for="campaign-incentivizationsLimit-inline" :error="inputError">
                            <template #default="{ id: inputId }">
                              <InputField
                                :id="inputId"
                                v-model.number="v$.incentivizationsLimit.$model"
                                :disabled="!formData.incentive || !formData.amount"
                                type="number"
                                :state="inputState"
                                :placeholder="t('Reviews.product.campaign.form.incentivizationsLimit.input.placeholder')"
                              >
                                <template #append><span class="text-subtlest">reviews</span></template>
                              </InputField>
                            </template>
                          </FieldWrapper>
                        </template>
                      </RadioCard>
                    </template>
                  </VuelidateField>
                  <VuelidateField :validation="v$.incentivizationsBudgetLimit">
                    <template #default="{ error: inputError, state: inputState }">
                      <RadioCard v-model="internalLimitSelection" value="setMaxBudget" :state="inputState">
                        {{ t('Reviews.product.campaign.form.limitType.input.option.setMaxBudget') }}
                        <template #nested>
                          <FieldWrapper for="campaign-incentivizationsBudgetLimit-inline" :error="inputError">
                            <template #default="{ id: inputId }">
                              <InputField
                                :id="inputId"
                                v-model.number="v$.incentivizationsBudgetLimit.$model"
                                :disabled="!formData.incentive || !formData.amount"
                                prepend-separator
                                type="number"
                                :state="inputState"
                                :placeholder="t('Reviews.product.campaign.form.incentivizationsBudgetLimit.input.placeholder')"
                              >
                                <template #prepend>
                                  <EuroIcon class="lucide-icon-s text-subtlest" :class="{ 'text-icon-disabled': incentivesFenced }" />
                                </template>
                              </InputField>
                            </template>
                          </FieldWrapper>
                        </template>
                      </RadioCard>
                    </template>
                  </VuelidateField>
                </fieldset>
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>

        <CampaignLimitPreviewBox
          v-if="formData.limitType && formData.incentivizationType"
          class="desktop:col-span-2"
          :incentive-amount="formData.amount ?? 0"
          :incentivization-type="formData.incentivizationType"
          :incentivizations-limit="formData.incentivizationsLimit ?? 0"
          :budget-limit="formData.incentivizationsBudgetLimit ?? 0"
          :variant="formData.limitType"
        />
      </div>
    </FeatureFencingWrapper>
  </div>
</template>
<script setup lang="ts">
import {
  FieldWrapper,
  InputField,
  DateField,
  RadioCard,
  SelectField,
  useCurrencyFormatter,
  MarkdownContent,
  TooltipWrapper,
} from '@ramp106/omrjs-core-ui'
import { EuroIcon, GiftIcon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import FencingBanner from '@/components/ReviewsCommon/Fencing/FencingBanner.vue'
import incentivesFencingImageUrl from '@/components/ReviewsCommon/Fencing/incentives_fencing.png'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { useReviewsCampaignFormOptions, NAME_MIN_LENGTH, NAME_MAX_LENGTH } from '@/composables/useReviewsCampaignFormOptions'
import {
  IncentivizationCampaignIncentivizationTypeEnum,
  IncentivizationCampaignLimitTypeEnum,
  IncentivizationCampaignPersonalizationLocaleEnum,
  type ProductData,
} from '@/gql/reviews'
import { maxLength, minLength, required, requiredIf, dateAfter, dateAfterOrEqual, minValue, maxValue } from '@/services/validators.ts'
import VuelidateField from '@/ui/VuelidateField.vue'

import CampaignLimitPreviewBox from './CampaignLimitPreviewBox.vue'
import type { CampaignFormData } from './types'

const props = defineProps<{
  initialData: CampaignFormData
  product: ProductData
}>()
const emit = defineEmits<{
  (e: 'change', value: CampaignFormData): void
}>()

const { t } = useI18n()
const { formatCurrency } = useCurrencyFormatter()

const { today, maxValidToDate, budgetEndDate, budgetEndDateLong, amazonAmountOptions, wunschFormattedValue, amazonFormattedValue } =
  useReviewsCampaignFormOptions(props.product)

const remainingBudget = computed(() => props.product.currentBudget?.remainingValue ?? 0)
const totalBudget = computed(() => (props.product.currentBudget?.value ?? 0) + (props.product.currentBudget?.additionalValue ?? 0))

const incentiveDescription = computed(() => {
  return incentivesFenced.value
    ? t('Reviews.product.campaign.form.incentive.fenced.description')
    : t('Reviews.product.campaign.form.incentive.description', {
        remainingBudget: formatCurrency(remainingBudget.value),
        totalBudget: formatCurrency(totalBudget.value),
        budgetEndDate: budgetEndDate.value,
      })
})

const formData = reactive<CampaignFormData>({
  ...props.initialData,
})

const incentiveSelection = ref<null | IncentivizationCampaignIncentivizationTypeEnum>(null)

const cachedAmount = ref<number | null>(null)

const { enabled: newTypesAvailable } = useFeatureFlag(FEATURE_FLAG.SHARED.NEW_INCENTIVIZATION_TYPES_RELEASE)

const AMOUNT_BEARING_TYPES: IncentivizationCampaignIncentivizationTypeEnum[] = [
  IncentivizationCampaignIncentivizationTypeEnum.Amazon,
  IncentivizationCampaignIncentivizationTypeEnum.Either,
  IncentivizationCampaignIncentivizationTypeEnum.Donation,
]

const isAmountBearing = (selection: IncentivizationCampaignIncentivizationTypeEnum | null): boolean =>
  selection !== null && AMOUNT_BEARING_TYPES.includes(selection)

const isNoneSelected = computed(() => incentiveSelection.value === IncentivizationCampaignIncentivizationTypeEnum.None)

const NEW_INCENTIVE_TYPES: IncentivizationCampaignIncentivizationTypeEnum[] = [
  IncentivizationCampaignIncentivizationTypeEnum.Either,
  IncentivizationCampaignIncentivizationTypeEnum.Donation,
  IncentivizationCampaignIncentivizationTypeEnum.None,
]

function alignSelectionWithBucket() {
  const selection = incentiveSelection.value
  if (newTypesAvailable.value && (selection === null || !NEW_INCENTIVE_TYPES.includes(selection))) {
    incentiveSelection.value = IncentivizationCampaignIncentivizationTypeEnum.None
  } else if (!newTypesAvailable.value && selection !== null && NEW_INCENTIVE_TYPES.includes(selection)) {
    incentiveSelection.value = null
  }
}

watch(newTypesAvailable, alignSelectionWithBucket)

const newIncentiveTypeCards = computed(() =>
  newTypesAvailable.value
    ? [
        {
          value: IncentivizationCampaignIncentivizationTypeEnum.Either,
          labelKey: 'Reviews.product.campaign.form.incentiveSelection.input.either',
          hasAmount: true,
        },
        {
          value: IncentivizationCampaignIncentivizationTypeEnum.Donation,
          labelKey: 'Reviews.product.campaign.form.incentiveSelection.input.donation',
          hasAmount: true,
        },
        {
          value: IncentivizationCampaignIncentivizationTypeEnum.None,
          labelKey: 'Reviews.product.campaign.form.incentiveSelection.input.none',
          hasAmount: false,
        },
      ]
    : [],
)

const internalLimitSelection = ref<'useRemainingBudget' | 'setByReviews' | 'setMaxBudget' | null>(null)

const cachedIncentivizationsLimit = ref<number | null>(null)
const cachedIncentivizationsBudgetLimit = ref<number | null>(null)

onMounted(() => {
  if (!props.initialData.incentive) {
    incentiveSelection.value = null
  } else {
    incentiveSelection.value = props.initialData.incentivizationType || null
  }

  alignSelectionWithBucket()

  if (isAmountBearing(incentiveSelection.value) && props.initialData.amount) {
    cachedAmount.value = props.initialData.amount
  }

  if (!formData.limitType) {
    internalLimitSelection.value = null
  } else if (formData.limitType === IncentivizationCampaignLimitTypeEnum.Budget) {
    internalLimitSelection.value = 'useRemainingBudget'
    formData.incentivizationsBudgetLimit = remainingBudget.value || undefined
  } else if (formData.limitType === IncentivizationCampaignLimitTypeEnum.Incentivizations) {
    internalLimitSelection.value = 'setByReviews'
  }
})

const maxIncentivizationsLimit = computed(() => {
  const amount = formData.amount ?? 1
  return Math.floor(remainingBudget.value / amount)
})

const maxBudgetLimit = computed(() => props.product.currentBudget?.remainingValue ?? 0)

const rules = computed(() => ({
  title: {
    required,
    minLength: minLength(NAME_MIN_LENGTH),
    maxLength: maxLength(NAME_MAX_LENGTH),
  },
  locale: {
    required,
  },
  validFrom: {
    required,
    dateAfterOrEqual: dateAfterOrEqual(today),
  },
  validTo: {
    dateAfter: dateAfter(formData.validFrom),
  },
  incentive: {},
  amount: {
    required: requiredIf(() => isAmountBearing(incentiveSelection.value)),
    minValue: minValue(10),
    maxValue: maxValue(100),
  },
  limitType: {},
  incentivizationsLimit: {
    required: requiredIf(() => formData.incentive && formData.limitType === IncentivizationCampaignLimitTypeEnum.Incentivizations),
    minValue: minValue(1),
    maxValue: maxValue(maxIncentivizationsLimit),
  },
  incentivizationsBudgetLimit: {
    required: requiredIf(() => formData.incentive && formData.limitType === IncentivizationCampaignLimitTypeEnum.Budget),
    minValue: minValue(1),
    maxValue: maxValue(maxBudgetLimit),
  },
}))

const $externalResults = ref<Partial<Record<keyof CampaignFormData, string>>>({})

const v$ = useVuelidate(rules, formData, { $externalResults })

function updateExternalErrors(errors: Partial<Record<keyof CampaignFormData, string>>) {
  $externalResults.value = errors
}

defineExpose({
  updateExternalErrors,
  validate: () => v$.value.$validate(),
  isValid: computed(() => !v$.value.$invalid),
  clearExternalErrors: () => v$.value.$clearExternalResults(),
})

watch(incentiveSelection, (newSelection) => {
  formData.incentive = newSelection !== null

  formData.incentivizationType = newSelection || undefined

  if (newSelection === IncentivizationCampaignIncentivizationTypeEnum.Wunsch) {
    formData.amount = 20
  } else if (isAmountBearing(newSelection)) {
    formData.amount = cachedAmount.value ?? 25
  } else {
    formData.amount = undefined
  }

  if (newSelection === null || newSelection === IncentivizationCampaignIncentivizationTypeEnum.None) {
    formData.limitType = null
    formData.incentivizationsLimit = undefined
    formData.incentivizationsBudgetLimit = undefined
    internalLimitSelection.value = null
  } else if (formData.limitType === null) {
    formData.limitType = IncentivizationCampaignLimitTypeEnum.Budget
    formData.incentivizationsBudgetLimit = remainingBudget.value
    internalLimitSelection.value = 'useRemainingBudget'
  }
})

watch(
  () => formData.amount,
  (newAmount) => {
    if (isAmountBearing(incentiveSelection.value) && newAmount !== undefined) {
      cachedAmount.value = newAmount
    }
  },
)

watch(internalLimitSelection, (newSelection) => {
  if (!newSelection) {
    formData.limitType = null
    formData.incentivizationsLimit = undefined
    formData.incentivizationsBudgetLimit = undefined
  } else if (newSelection === 'useRemainingBudget') {
    formData.limitType = IncentivizationCampaignLimitTypeEnum.Budget
    formData.incentivizationsBudgetLimit = remainingBudget.value || undefined
    formData.incentivizationsLimit = undefined
  } else if (newSelection === 'setByReviews') {
    formData.limitType = IncentivizationCampaignLimitTypeEnum.Incentivizations
    formData.incentivizationsLimit = cachedIncentivizationsLimit.value
    formData.incentivizationsBudgetLimit = undefined
  } else if (newSelection === 'setMaxBudget') {
    formData.limitType = IncentivizationCampaignLimitTypeEnum.Budget
    formData.incentivizationsBudgetLimit = cachedIncentivizationsBudgetLimit.value
    formData.incentivizationsLimit = undefined
  }
})

watch(
  () => formData.incentivizationsLimit,
  (newValue) => {
    if (newValue !== undefined && internalLimitSelection.value === 'setByReviews') {
      cachedIncentivizationsLimit.value = newValue
    }
  },
)

watch(
  () => formData.incentivizationsBudgetLimit,
  (newValue) => {
    if (newValue !== undefined && internalLimitSelection.value === 'setMaxBudget') {
      cachedIncentivizationsBudgetLimit.value = newValue
    }
  },
)

watch(
  formData,
  () => {
    emit('change', { ...formData })
  },
  { deep: true },
)

const incentivesFenced = computed(() => {
  return !props.product.hasPaidPlan
})

const fencedFeatureList = computed(() => [
  t('Reviews.product.view.media.banner.incentivesFencingOne'),
  t('Reviews.product.view.media.banner.incentivesFencingTwo'),
  t('Reviews.product.view.media.banner.incentivesFencingThree'),
])
</script>
